import { supabase } from '../lib/supabase'
import { toast } from 'sonner'
import type {
  GitHubIssue,
  CreateIssueInput,
  UpdateIssueInput,
  IssueState,
  GitHubApiResponse,
} from '../types/github'

const EDGE_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/github-issues`

/**
 * Appelle l'Edge Function Supabase pour interagir avec GitHub
 */
async function callEdgeFunction<T = any>(body: any): Promise<GitHubApiResponse<T>> {
  try {
    // Récupération de la session pour l'auth header
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('Vous devez être connecté')
    }

    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(body),
    })

    const data = await response.json()

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Une erreur est survenue')
    }

    return data
  } catch (error: any) {
    console.error('Edge Function error:', error)
    throw error
  }
}

/**
 * Récupère la liste des issues GitHub
 */
export async function fetchIssues(state: IssueState = 'open'): Promise<GitHubIssue[]> {
  try {
    const response = await callEdgeFunction<GitHubIssue[]>({
      action: 'list',
      state,
    })

    return response.data || []
  } catch (error: any) {
    console.error('Error fetching issues:', error)
    toast.error(error.message || 'Erreur lors de la récupération des tickets')
    return []
  }
}

/**
 * Crée une nouvelle issue GitHub
 */
export async function createIssue(input: CreateIssueInput): Promise<GitHubIssue | null> {
  try {
    const { page, attachmentUrl, body, title, type, labels } = input

    // Trouver les informations de la page
    let pageName: string | undefined
    let pageUrl: string | undefined

    if (page) {
      // Parser le format "Page Name|/path|label"
      const parts = page.split('|')
      if (parts.length >= 2) {
        pageName = parts[0]
        const path = parts[1]
        pageUrl = path !== 'null' ? `${window.location.origin}${path}` : undefined
      }
    }

    const response = await callEdgeFunction<GitHubIssue>({
      action: 'create',
      title,
      description: body, // L'Edge Function attend "description", pas "body"
      type,
      page: labels?.[0], // Le label de la page (ex: "page:contact")
      pageName,
      pageUrl,
      attachmentUrl,
    })

    if (response.data) {
      toast.success('Ticket créé avec succès')
      return response.data
    }

    return null
  } catch (error: any) {
    console.error('Error creating issue:', error)
    toast.error(error.message || 'Erreur lors de la création du ticket')
    return null
  }
}

/**
 * Met à jour une issue existante
 */
export async function updateIssue(input: UpdateIssueInput): Promise<GitHubIssue | null> {
  try {
    const { issueNumber, ...updates } = input

    const response = await callEdgeFunction<GitHubIssue>({
      action: 'update',
      issueNumber,
      updates,
    })

    if (response.data) {
      toast.success('Ticket mis à jour')
      return response.data
    }

    return null
  } catch (error: any) {
    console.error('Error updating issue:', error)
    toast.error(error.message || 'Erreur lors de la mise à jour du ticket')
    return null
  }
}

/**
 * Ferme une issue
 */
export async function closeIssue(issueNumber: number): Promise<GitHubIssue | null> {
  try {
    const response = await callEdgeFunction<GitHubIssue>({
      action: 'close',
      issueNumber,
    })

    if (response.data) {
      toast.success('Ticket fermé')
      return response.data
    }

    return null
  } catch (error: any) {
    console.error('Error closing issue:', error)
    toast.error(error.message || 'Erreur lors de la fermeture du ticket')
    return null
  }
}

/**
 * Supprime une issue
 */
export async function deleteIssue(issueNumber: number): Promise<boolean> {
  try {
    const response = await callEdgeFunction<void>({
      action: 'delete',
      issueNumber,
    })

    if (response.success) {
      toast.success('Ticket supprimé')
      return true
    }

    return false
  } catch (error: any) {
    console.error('Error deleting issue:', error)
    toast.error(error.message || 'Erreur lors de la suppression du ticket')
    return false
  }
}

/**
 * Assure que tous les labels requis existent dans le repo GitHub
 * Appelé au chargement de la page Ticketing
 */
export async function ensureLabels(): Promise<void> {
  try {
    const response = await callEdgeFunction<{
      created: number
      total: number
      labels: string[]
    }>({
      action: 'ensure-labels',
    })

    if (response.data && response.data.created > 0) {
      console.log(`Created ${response.data.created} labels:`, response.data.labels)
    }
  } catch (error: any) {
    console.error('Error ensuring labels:', error)
    // Ne pas afficher de toast ici car c'est une opération silencieuse en background
  }
}
